// Comprehensive Workout Categorization System
// Based on Sports Medicine & Kinesiology Principles

export interface WorkoutCategory {
  id: string;
  name: string;
  description: string;
  color: string;
  subcategories: WorkoutSubcategory[];
}

export interface WorkoutSubcategory {
  id: string;
  name: string;
  description: string;
  tags: string[];
  equipment?: string[];
  targetAreas?: string[];
  intensity?: 'low' | 'moderate' | 'high' | 'variable';
  duration?: string;
}

// COMPREHENSIVE WORKOUT CATEGORIZATION SYSTEM
export const WORKOUT_CATEGORIES: WorkoutCategory[] = [
  
  // 1. CARDIOVASCULAR TRAINING
  {
    id: 'cardio',
    name: 'Cardiovascular Training',
    description: 'Aerobic and anaerobic conditioning for cardiovascular health and endurance',
    color: 'bg-red-100 text-red-800',
    subcategories: [
      // Running Specialization
      {
        id: 'running-base',
        name: 'Base Running',
        description: 'Easy-paced aerobic runs for building cardiovascular base',
        tags: ['running', 'aerobic', 'base-building', 'easy-pace', 'zone-2'],
        equipment: ['running shoes', 'optional: heart rate monitor'],
        targetAreas: ['cardiovascular system', 'legs', 'core'],
        intensity: 'low',
        duration: '30-90 minutes'
      },
      {
        id: 'running-tempo',
        name: 'Tempo Runs',
        description: 'Comfortably hard sustained efforts at lactate threshold',
        tags: ['running', 'tempo', 'threshold', 'lactate', 'sustained'],
        equipment: ['running shoes', 'heart rate monitor'],
        targetAreas: ['cardiovascular system', 'legs', 'lactate system'],
        intensity: 'moderate',
        duration: '20-60 minutes'
      },
      {
        id: 'running-intervals',
        name: 'Interval Training',
        description: 'High-intensity work periods with recovery intervals',
        tags: ['running', 'intervals', 'vo2max', 'speed', 'anaerobic'],
        equipment: ['running shoes', 'track or measured distance'],
        targetAreas: ['cardiovascular system', 'legs', 'anaerobic system'],
        intensity: 'high',
        duration: '30-60 minutes'
      },
      {
        id: 'running-sprints',
        name: 'Sprint Training',
        description: 'Maximum velocity and neuromuscular power development',
        tags: ['running', 'sprints', 'speed', 'power', 'neuromuscular'],
        equipment: ['running shoes', 'track or flat surface'],
        targetAreas: ['neuromuscular system', 'legs', 'power'],
        intensity: 'high',
        duration: '20-45 minutes'
      },
      {
        id: 'running-long',
        name: 'Long Runs',
        description: 'Extended duration runs for aerobic capacity and mental toughness',
        tags: ['running', 'long-run', 'endurance', 'aerobic', 'mental'],
        equipment: ['running shoes', 'hydration', 'nutrition'],
        targetAreas: ['cardiovascular system', 'legs', 'mental endurance'],
        intensity: 'low',
        duration: '60-180+ minutes'
      },
      {
        id: 'running-fartlek',
        name: 'Fartlek Training',
        description: 'Unstructured speed play with varied intensities',
        tags: ['running', 'fartlek', 'speed-play', 'varied', 'unstructured'],
        equipment: ['running shoes'],
        targetAreas: ['cardiovascular system', 'legs', 'adaptability'],
        intensity: 'variable',
        duration: '30-60 minutes'
      },
      
      // Cycling Specialization
      {
        id: 'cycling-base',
        name: 'Base Cycling',
        description: 'Steady aerobic cycling for cardiovascular base building',
        tags: ['cycling', 'aerobic', 'base', 'endurance', 'zone-2'],
        equipment: ['bicycle', 'helmet', 'optional: power meter'],
        targetAreas: ['cardiovascular system', 'legs', 'glutes'],
        intensity: 'low',
        duration: '45-120 minutes'
      },
      {
        id: 'cycling-intervals',
        name: 'Cycling Intervals',
        description: 'High-intensity cycling intervals for power and VO2max',
        tags: ['cycling', 'intervals', 'power', 'vo2max', 'anaerobic'],
        equipment: ['bicycle', 'helmet', 'power meter or heart rate monitor'],
        targetAreas: ['cardiovascular system', 'legs', 'anaerobic system'],
        intensity: 'high',
        duration: '45-90 minutes'
      },
      {
        id: 'cycling-hill',
        name: 'Hill Cycling',
        description: 'Climbing-focused cycling for strength and power',
        tags: ['cycling', 'hills', 'climbing', 'strength', 'power'],
        equipment: ['bicycle', 'helmet'],
        targetAreas: ['legs', 'glutes', 'core', 'cardiovascular system'],
        intensity: 'high',
        duration: '60-120 minutes'
      },
      {
        id: 'cycling-recovery',
        name: 'Recovery Cycling',
        description: 'Easy-paced cycling for active recovery',
        tags: ['cycling', 'recovery', 'easy', 'active-recovery', 'zone-1'],
        equipment: ['bicycle', 'helmet'],
        targetAreas: ['cardiovascular system', 'legs'],
        intensity: 'low',
        duration: '30-60 minutes'
      },
      
      // Swimming Specialization
      {
        id: 'swimming-technique',
        name: 'Technique Swimming',
        description: 'Stroke technique development and efficiency',
        tags: ['swimming', 'technique', 'form', 'efficiency', 'drills'],
        equipment: ['swimsuit', 'goggles', 'kickboard', 'pull buoy'],
        targetAreas: ['full body', 'cardiovascular system', 'technique'],
        intensity: 'low',
        duration: '30-60 minutes'
      },
      {
        id: 'swimming-endurance',
        name: 'Endurance Swimming',
        description: 'Continuous swimming for aerobic capacity',
        tags: ['swimming', 'endurance', 'aerobic', 'continuous', 'distance'],
        equipment: ['swimsuit', 'goggles'],
        targetAreas: ['cardiovascular system', 'full body', 'endurance'],
        intensity: 'moderate',
        duration: '45-90 minutes'
      },
      {
        id: 'swimming-intervals',
        name: 'Swimming Intervals',
        description: 'High-intensity swimming intervals for speed and power',
        tags: ['swimming', 'intervals', 'speed', 'power', 'anaerobic'],
        equipment: ['swimsuit', 'goggles', 'pace clock'],
        targetAreas: ['cardiovascular system', 'full body', 'anaerobic system'],
        intensity: 'high',
        duration: '45-75 minutes'
      },
      {
        id: 'swimming-sprint',
        name: 'Sprint Swimming',
        description: 'Maximum velocity swimming for neuromuscular power',
        tags: ['swimming', 'sprints', 'speed', 'power', 'neuromuscular'],
        equipment: ['swimsuit', 'goggles', 'pace clock'],
        targetAreas: ['neuromuscular system', 'full body', 'power'],
        intensity: 'high',
        duration: '30-60 minutes'
      },
      
      // General Cardio
      {
        id: 'hiit',
        name: 'High-Intensity Interval Training',
        description: 'Short bursts of intense exercise with recovery periods',
        tags: ['hiit', 'intervals', 'intense', 'metabolic', 'time-efficient'],
        equipment: ['bodyweight', 'optional: dumbbells', 'timer'],
        targetAreas: ['cardiovascular system', 'full body', 'metabolic'],
        intensity: 'high',
        duration: '15-45 minutes'
      },
      {
        id: 'steady-state',
        name: 'Steady-State Cardio',
        description: 'Continuous moderate-intensity cardiovascular exercise',
        tags: ['steady-state', 'continuous', 'moderate', 'fat-burning', 'aerobic'],
        equipment: ['varies by activity'],
        targetAreas: ['cardiovascular system', 'fat metabolism'],
        intensity: 'moderate',
        duration: '30-60 minutes'
      }
    ]
  },

  // 2. STRENGTH TRAINING
  {
    id: 'strength',
    name: 'Strength Training',
    description: 'Resistance training for muscle strength, power, and hypertrophy',
    color: 'bg-purple-100 text-purple-800',
    subcategories: [
      // By Body Region
      {
        id: 'upper-body',
        name: 'Upper Body Strength',
        description: 'Strength training for chest, back, shoulders, and arms',
        tags: ['upper-body', 'chest', 'back', 'shoulders', 'arms', 'push', 'pull'],
        equipment: ['dumbbells', 'barbell', 'bench', 'pull-up bar'],
        targetAreas: ['chest', 'back', 'shoulders', 'biceps', 'triceps'],
        intensity: 'moderate',
        duration: '45-75 minutes'
      },
      {
        id: 'lower-body',
        name: 'Lower Body Strength',
        description: 'Strength training for legs, glutes, and hips',
        tags: ['lower-body', 'legs', 'glutes', 'quads', 'hamstrings', 'calves'],
        equipment: ['barbell', 'dumbbells', 'squat rack', 'leg press'],
        targetAreas: ['quadriceps', 'hamstrings', 'glutes', 'calves', 'hips'],
        intensity: 'moderate',
        duration: '45-75 minutes'
      },
      {
        id: 'core-strength',
        name: 'Core Strength',
        description: 'Targeted training for abdominals, obliques, and deep stabilizers',
        tags: ['core', 'abs', 'obliques', 'stability', 'anti-rotation', 'anti-extension'],
        equipment: ['bodyweight', 'medicine ball', 'resistance bands', 'stability ball'],
        targetAreas: ['rectus abdominis', 'obliques', 'transverse abdominis', 'erector spinae'],
        intensity: 'moderate',
        duration: '20-45 minutes'
      },
      {
        id: 'back-strength',
        name: 'Back Strength',
        description: 'Comprehensive back training for posture and strength',
        tags: ['back', 'lats', 'rhomboids', 'traps', 'posture', 'pulling'],
        equipment: ['pull-up bar', 'dumbbells', 'barbell', 'cable machine'],
        targetAreas: ['latissimus dorsi', 'rhomboids', 'trapezius', 'rear deltoids'],
        intensity: 'moderate',
        duration: '45-60 minutes'
      },
      {
        id: 'full-body-strength',
        name: 'Full Body Strength',
        description: 'Compound movements targeting multiple muscle groups',
        tags: ['full-body', 'compound', 'functional', 'multi-joint', 'efficient'],
        equipment: ['barbell', 'dumbbells', 'kettlebells'],
        targetAreas: ['full body', 'multiple muscle groups'],
        intensity: 'moderate',
        duration: '60-90 minutes'
      },
      
      // By Training Style
      {
        id: 'powerlifting',
        name: 'Powerlifting',
        description: 'Focus on squat, bench press, and deadlift for maximum strength',
        tags: ['powerlifting', 'squat', 'bench', 'deadlift', 'max-strength', '1rm'],
        equipment: ['barbell', 'squat rack', 'bench', 'plates'],
        targetAreas: ['full body', 'maximum strength'],
        intensity: 'high',
        duration: '60-120 minutes'
      },
      {
        id: 'olympic-lifting',
        name: 'Olympic Lifting',
        description: 'Clean, jerk, and snatch for explosive power development',
        tags: ['olympic', 'clean', 'jerk', 'snatch', 'explosive', 'power'],
        equipment: ['barbell', 'bumper plates', 'lifting platform'],
        targetAreas: ['full body', 'explosive power'],
        intensity: 'high',
        duration: '60-90 minutes'
      },
      {
        id: 'bodyweight-strength',
        name: 'Bodyweight Strength',
        description: 'Strength training using body weight as resistance',
        tags: ['bodyweight', 'calisthenics', 'no-equipment', 'functional', 'progressive'],
        equipment: ['bodyweight', 'optional: pull-up bar'],
        targetAreas: ['full body', 'functional strength'],
        intensity: 'variable',
        duration: '30-60 minutes'
      },
      {
        id: 'kettlebell',
        name: 'Kettlebell Training',
        description: 'Dynamic strength and conditioning with kettlebells',
        tags: ['kettlebell', 'dynamic', 'ballistic', 'conditioning', 'functional'],
        equipment: ['kettlebells'],
        targetAreas: ['full body', 'posterior chain', 'core'],
        intensity: 'moderate',
        duration: '30-60 minutes'
      },
      
      // By Equipment/Setting
      {
        id: 'gym-strength',
        name: 'Gym-Based Strength',
        description: 'Full gym equipment strength training',
        tags: ['gym', 'machines', 'free-weights', 'comprehensive', 'variety'],
        equipment: ['full gym access', 'machines', 'free weights'],
        targetAreas: ['varies by workout'],
        intensity: 'variable',
        duration: '45-90 minutes'
      },
      {
        id: 'home-strength',
        name: 'Home Strength Training',
        description: 'Strength training with minimal home equipment',
        tags: ['home', 'minimal-equipment', 'dumbbells', 'resistance-bands', 'convenient'],
        equipment: ['dumbbells', 'resistance bands', 'bodyweight'],
        targetAreas: ['full body'],
        intensity: 'moderate',
        duration: '30-60 minutes'
      }
    ]
  },

  // 3. RECREATIONAL SPORTS
  {
    id: 'sports',
    name: 'Recreational Sports',
    description: 'Sport-specific activities and skill development',
    color: 'bg-orange-100 text-orange-800',
    subcategories: [
      // Team Sports
      {
        id: 'basketball',
        name: 'Basketball',
        description: 'Pickup games, skill work, and basketball-specific training',
        tags: ['basketball', 'team-sport', 'agility', 'jumping', 'coordination'],
        equipment: ['basketball', 'court', 'basketball shoes'],
        targetAreas: ['legs', 'cardiovascular system', 'agility', 'coordination'],
        intensity: 'high',
        duration: '60-120 minutes'
      },
      {
        id: 'soccer',
        name: 'Soccer/Football',
        description: 'Soccer games, drills, and conditioning',
        tags: ['soccer', 'football', 'team-sport', 'running', 'agility', 'endurance'],
        equipment: ['soccer ball', 'cleats', 'field'],
        targetAreas: ['legs', 'cardiovascular system', 'agility'],
        intensity: 'high',
        duration: '60-120 minutes'
      },
      {
        id: 'volleyball',
        name: 'Volleyball',
        description: 'Volleyball games and skill development',
        tags: ['volleyball', 'team-sport', 'jumping', 'coordination', 'reaction'],
        equipment: ['volleyball', 'net', 'court'],
        targetAreas: ['legs', 'shoulders', 'core', 'coordination'],
        intensity: 'moderate',
        duration: '60-90 minutes'
      },
      {
        id: 'tennis',
        name: 'Tennis',
        description: 'Tennis matches, lessons, and practice',
        tags: ['tennis', 'racquet-sport', 'agility', 'hand-eye', 'lateral-movement'],
        equipment: ['tennis racquet', 'tennis balls', 'court'],
        targetAreas: ['full body', 'agility', 'coordination'],
        intensity: 'moderate',
        duration: '60-120 minutes'
      },
      {
        id: 'badminton',
        name: 'Badminton',
        description: 'Badminton games and skill practice',
        tags: ['badminton', 'racquet-sport', 'agility', 'reaction', 'overhead'],
        equipment: ['badminton racquet', 'shuttlecock', 'court'],
        targetAreas: ['shoulders', 'legs', 'agility', 'coordination'],
        intensity: 'moderate',
        duration: '45-90 minutes'
      },
      
      // Individual Sports
      {
        id: 'golf',
        name: 'Golf',
        description: 'Golf rounds, practice, and skill development',
        tags: ['golf', 'precision', 'rotation', 'walking', 'mental'],
        equipment: ['golf clubs', 'golf balls', 'course/range'],
        targetAreas: ['core', 'shoulders', 'mental focus'],
        intensity: 'low',
        duration: '120-300 minutes'
      },
      {
        id: 'rock-climbing',
        name: 'Rock Climbing',
        description: 'Indoor/outdoor climbing for strength and problem-solving',
        tags: ['climbing', 'bouldering', 'strength', 'problem-solving', 'grip'],
        equipment: ['climbing shoes', 'harness', 'chalk'],
        targetAreas: ['upper body', 'core', 'grip strength', 'mental'],
        intensity: 'moderate',
        duration: '60-180 minutes'
      },
      {
        id: 'martial-arts',
        name: 'Martial Arts',
        description: 'Various martial arts training and practice',
        tags: ['martial-arts', 'technique', 'flexibility', 'discipline', 'self-defense'],
        equipment: ['varies by discipline'],
        targetAreas: ['full body', 'flexibility', 'coordination'],
        intensity: 'moderate',
        duration: '60-90 minutes'
      },
      
      // Recreational Activities
      {
        id: 'hiking',
        name: 'Hiking',
        description: 'Trail hiking and nature walking',
        tags: ['hiking', 'nature', 'endurance', 'outdoor', 'mental-health'],
        equipment: ['hiking boots', 'backpack', 'water'],
        targetAreas: ['legs', 'cardiovascular system', 'mental health'],
        intensity: 'variable',
        duration: '60-480 minutes'
      },
      {
        id: 'dancing',
        name: 'Dancing',
        description: 'Various dance styles for fitness and fun',
        tags: ['dancing', 'rhythm', 'coordination', 'cardio', 'expression'],
        equipment: ['music', 'dance shoes'],
        targetAreas: ['full body', 'cardiovascular system', 'coordination'],
        intensity: 'moderate',
        duration: '45-90 minutes'
      }
    ]
  },

  // 4. FLEXIBILITY & MOBILITY
  {
    id: 'flexibility',
    name: 'Flexibility & Mobility',
    description: 'Stretching, mobility work, and movement quality',
    color: 'bg-pink-100 text-pink-800',
    subcategories: [
      {
        id: 'static-stretching',
        name: 'Static Stretching',
        description: 'Hold stretches for improved flexibility',
        tags: ['static', 'flexibility', 'hold', 'passive', 'range-of-motion'],
        equipment: ['yoga mat', 'optional: straps'],
        targetAreas: ['varies by routine'],
        intensity: 'low',
        duration: '15-45 minutes'
      },
      {
        id: 'dynamic-warmup',
        name: 'Dynamic Warm-up',
        description: 'Movement-based preparation for activity',
        tags: ['dynamic', 'warmup', 'movement', 'preparation', 'activation'],
        equipment: ['bodyweight'],
        targetAreas: ['full body', 'movement preparation'],
        intensity: 'low',
        duration: '10-20 minutes'
      },
      {
        id: 'yoga-flow',
        name: 'Yoga Flow',
        description: 'Flowing yoga sequences for flexibility and mindfulness',
        tags: ['yoga', 'flow', 'flexibility', 'mindfulness', 'breathing'],
        equipment: ['yoga mat', 'optional: blocks'],
        targetAreas: ['full body', 'flexibility', 'mental'],
        intensity: 'low',
        duration: '30-90 minutes'
      },
      {
        id: 'foam-rolling',
        name: 'Foam Rolling',
        description: 'Self-myofascial release for recovery',
        tags: ['foam-rolling', 'smr', 'recovery', 'myofascial', 'trigger-point'],
        equipment: ['foam roller', 'massage balls'],
        targetAreas: ['varies by routine'],
        intensity: 'low',
        duration: '10-30 minutes'
      }
    ]
  },

  // 5. RECOVERY & REGENERATION
  {
    id: 'recovery',
    name: 'Recovery & Regeneration',
    description: 'Active recovery and regeneration protocols',
    color: 'bg-green-100 text-green-800',
    subcategories: [
      {
        id: 'active-recovery',
        name: 'Active Recovery',
        description: 'Low-intensity movement for recovery',
        tags: ['active-recovery', 'low-intensity', 'movement', 'circulation'],
        equipment: ['varies'],
        targetAreas: ['circulation', 'recovery'],
        intensity: 'low',
        duration: '20-45 minutes'
      },
      {
        id: 'breathing-work',
        name: 'Breathing & Meditation',
        description: 'Breathwork and meditation for recovery',
        tags: ['breathing', 'meditation', 'stress-relief', 'parasympathetic'],
        equipment: ['none'],
        targetAreas: ['nervous system', 'mental'],
        intensity: 'low',
        duration: '10-30 minutes'
      },
      {
        id: 'gentle-movement',
        name: 'Gentle Movement',
        description: 'Easy movement patterns for recovery days',
        tags: ['gentle', 'easy', 'movement', 'recovery-day', 'maintenance'],
        equipment: ['minimal'],
        targetAreas: ['full body', 'maintenance'],
        intensity: 'low',
        duration: '20-40 minutes'
      }
    ]
  },

  // 6. FUNCTIONAL TRAINING
  {
    id: 'functional',
    name: 'Functional Training',
    description: 'Movement patterns for daily life and athletic performance',
    color: 'bg-blue-100 text-blue-800',
    subcategories: [
      {
        id: 'movement-patterns',
        name: 'Movement Patterns',
        description: 'Fundamental human movement patterns',
        tags: ['movement-patterns', 'squat', 'hinge', 'push', 'pull', 'carry'],
        equipment: ['varies'],
        targetAreas: ['functional movement'],
        intensity: 'moderate',
        duration: '30-60 minutes'
      },
      {
        id: 'balance-stability',
        name: 'Balance & Stability',
        description: 'Proprioception and stability training',
        tags: ['balance', 'stability', 'proprioception', 'single-leg', 'unstable'],
        equipment: ['balance board', 'bosu ball', 'stability ball'],
        targetAreas: ['stabilizers', 'proprioception'],
        intensity: 'low',
        duration: '20-45 minutes'
      },
      {
        id: 'plyometrics',
        name: 'Plyometric Training',
        description: 'Explosive jumping and bounding exercises',
        tags: ['plyometrics', 'jumping', 'explosive', 'power', 'reactive'],
        equipment: ['bodyweight', 'optional: boxes'],
        targetAreas: ['power', 'explosiveness', 'legs'],
        intensity: 'high',
        duration: '20-45 minutes'
      }
    ]
  }
];

// EQUIPMENT CATEGORIES
export const EQUIPMENT_CATEGORIES = {
  bodyweight: ['bodyweight', 'no equipment needed'],
  basic_home: ['dumbbells', 'resistance bands', 'yoga mat', 'stability ball'],
  cardio_equipment: ['treadmill', 'stationary bike', 'elliptical', 'rowing machine'],
  strength_equipment: ['barbell', 'dumbbells', 'kettlebells', 'weight plates', 'bench'],
  gym_access: ['full gym', 'cable machines', 'smith machine', 'leg press', 'lat pulldown'],
  sport_specific: ['basketball', 'tennis racquet', 'golf clubs', 'climbing gear'],
  recovery_tools: ['foam roller', 'massage balls', 'resistance bands', 'yoga blocks'],
  outdoor: ['running shoes', 'hiking boots', 'bicycle', 'helmet']
};

// TARGET AREAS MAPPING
export const TARGET_AREAS = {
  // Primary Muscle Groups
  chest: ['pectorals', 'chest muscles'],
  back: ['latissimus dorsi', 'rhomboids', 'trapezius', 'erector spinae'],
  shoulders: ['deltoids', 'rotator cuff'],
  arms: ['biceps', 'triceps', 'forearms'],
  core: ['rectus abdominis', 'obliques', 'transverse abdominis', 'deep stabilizers'],
  legs: ['quadriceps', 'hamstrings', 'glutes', 'calves'],
  
  // Systems
  cardiovascular: ['heart', 'lungs', 'circulation', 'aerobic capacity'],
  nervous: ['coordination', 'balance', 'proprioception', 'reaction time'],
  metabolic: ['energy systems', 'fat burning', 'metabolic conditioning'],
  
  // Functional Areas
  posture: ['postural muscles', 'alignment', 'desk worker relief'],
  athletic: ['power', 'speed', 'agility', 'sport performance'],
  daily_life: ['functional movement', 'activities of daily living']
};

// SERVICE CLASS
export class WorkoutCategorizationService {
  
  /**
   * Get all workout categories
   */
  static getAllCategories(): WorkoutCategory[] {
    return WORKOUT_CATEGORIES;
  }
  
  /**
   * Get category by ID
   */
  static getCategoryById(id: string): WorkoutCategory | undefined {
    return WORKOUT_CATEGORIES.find(cat => cat.id === id);
  }
  
  /**
   * Get subcategory by ID
   */
  static getSubcategoryById(categoryId: string, subcategoryId: string): WorkoutSubcategory | undefined {
    const category = this.getCategoryById(categoryId);
    return category?.subcategories.find(sub => sub.id === subcategoryId);
  }
  
  /**
   * Search subcategories by tags
   */
  static searchByTags(tags: string[]): WorkoutSubcategory[] {
    const results: WorkoutSubcategory[] = [];
    
    WORKOUT_CATEGORIES.forEach(category => {
      category.subcategories.forEach(subcategory => {
        const hasMatchingTag = tags.some(tag => 
          subcategory.tags.some(subTag => 
            subTag.toLowerCase().includes(tag.toLowerCase())
          )
        );
        if (hasMatchingTag) {
          results.push(subcategory);
        }
      });
    });
    
    return results;
  }
  
  /**
   * Get subcategories by equipment available
   */
  static getByEquipment(availableEquipment: string[]): WorkoutSubcategory[] {
    const results: WorkoutSubcategory[] = [];
    
    WORKOUT_CATEGORIES.forEach(category => {
      category.subcategories.forEach(subcategory => {
        if (!subcategory.equipment || subcategory.equipment.length === 0) {
          results.push(subcategory); // Bodyweight exercises
          return;
        }
        
        const canPerform = subcategory.equipment.every(equipment =>
          availableEquipment.some(available =>
            available.toLowerCase().includes(equipment.toLowerCase()) ||
            equipment.toLowerCase().includes(available.toLowerCase())
          )
        );
        
        if (canPerform) {
          results.push(subcategory);
        }
      });
    });
    
    return results;
  }
  
  /**
   * Get subcategories by target areas
   */
  static getByTargetAreas(targetAreas: string[]): WorkoutSubcategory[] {
    const results: WorkoutSubcategory[] = [];
    
    WORKOUT_CATEGORIES.forEach(category => {
      category.subcategories.forEach(subcategory => {
        if (!subcategory.targetAreas) return;
        
        const hasMatchingArea = targetAreas.some(target =>
          subcategory.targetAreas!.some(area =>
            area.toLowerCase().includes(target.toLowerCase()) ||
            target.toLowerCase().includes(area.toLowerCase())
          )
        );
        
        if (hasMatchingArea) {
          results.push(subcategory);
        }
      });
    });
    
    return results;
  }
  
  /**
   * Generate LLM prompt for workout content
   */
  static generateWorkoutPrompt(params: {
    subcategoryId: string;
    categoryId: string;
    userGender?: string;
    duration?: string;
    intensity?: string;
    equipment?: string[];
    targetAreas?: string[];
  }): string {
    const { subcategoryId, categoryId, userGender, duration, intensity, equipment, targetAreas } = params;
    
    const category = this.getCategoryById(categoryId);
    const subcategory = this.getSubcategoryById(categoryId, subcategoryId);
    
    if (!category || !subcategory) {
      throw new Error('Invalid category or subcategory ID');
    }
    
    const basePrompt = `Generate a comprehensive ${subcategory.name} workout.

**Workout Type:** ${subcategory.name}
**Category:** ${category.name}
**Description:** ${subcategory.description}

Please provide:
1. Workout overview and objectives
2. Detailed exercise list with:
   - Exercise name and description
   - Sets, reps, or duration as appropriate
   - Rest periods
   - Form cues and technique points
   - Safety considerations
3. Warm-up recommendations (5-10 minutes)
4. Cool-down and stretching (5-10 minutes)
5. Progression options for different fitness levels
6. Modification options for limitations or injuries

**Workout Parameters:**
- Duration: ${duration || subcategory.duration || '45-60 minutes'}
- Intensity: ${intensity || subcategory.intensity || 'moderate'}
- Equipment: ${equipment?.join(', ') || subcategory.equipment?.join(', ') || 'bodyweight'}
- Target Areas: ${targetAreas?.join(', ') || subcategory.targetAreas?.join(', ') || 'varies'}
- Tags: ${subcategory.tags.join(', ')}`;

    // Add gender-aware media instructions if needed
    if (userGender) {
      const genderInstruction = userGender.toLowerCase() === 'male' 
        ? 'When providing exercise demonstration images, feature male demonstrators and athletes.'
        : userGender.toLowerCase() === 'female'
        ? 'When providing exercise demonstration images, feature female demonstrators and athletes.'
        : 'When providing exercise demonstration images, alternate between male and female demonstrators.';
      
      return basePrompt + `\n\n**Media Selection:** ${genderInstruction}`;
    }
    
    return basePrompt;
  }
  
  /**
   * Get workout type configuration for UI (simplified version)
   */
  static getWorkoutTypeConfigSimple(workoutType: string) {
    // Map simplified workout types to our categorization system
    const typeMapping: Record<string, { categoryId: string; subcategoryId?: string; fallback?: any }> = {
      // Cardiovascular types
      'base-cardio': { categoryId: 'cardio', subcategoryId: 'running-base' },
      'tempo-cardio': { categoryId: 'cardio', subcategoryId: 'running-tempo' },
      'interval-cardio': { categoryId: 'cardio', subcategoryId: 'running-intervals' },
      'long-cardio': { categoryId: 'cardio', subcategoryId: 'running-long' },
      'hiit-training': { categoryId: 'cardio', subcategoryId: 'hiit' },
      'circuit-training': { categoryId: 'cardio', subcategoryId: 'hiit' },
      
      // Strength types
      'upper-push': { categoryId: 'strength', subcategoryId: 'upper-body' },
      'upper-pull': { categoryId: 'strength', subcategoryId: 'back-strength' },
      'lower-body': { categoryId: 'strength', subcategoryId: 'lower-body' },
      'full-body-strength': { categoryId: 'strength', subcategoryId: 'full-body' },
      'powerlifting': { categoryId: 'strength', subcategoryId: 'powerlifting' },
      'olympic-lifting': { categoryId: 'strength', subcategoryId: 'olympic-lifting' },
      'bodyweight-strength': { categoryId: 'strength', subcategoryId: 'bodyweight' },
      
      // Sports types
      'basketball': { categoryId: 'sports', subcategoryId: 'basketball' },
      'soccer': { categoryId: 'sports', subcategoryId: 'soccer' },
      'tennis': { categoryId: 'sports', subcategoryId: 'tennis' },
      'volleyball': { categoryId: 'sports', subcategoryId: 'volleyball' },
      'swimming-laps': { categoryId: 'cardio', subcategoryId: 'swimming-endurance' },
      'cycling-road': { categoryId: 'cardio', subcategoryId: 'cycling-base' },
      'rock-climbing': { categoryId: 'sports', subcategoryId: 'rock-climbing' },
      
      // 🎯 TYPE FIX (Nov 2025): Added missing flexibility/mobility types
      'static-stretching': { categoryId: 'flexibility', subcategoryId: 'static-stretching' },
      'dynamic-warmup': { categoryId: 'flexibility', subcategoryId: 'dynamic-stretching' },
      'yoga-flow': { categoryId: 'flexibility', subcategoryId: 'yoga' },
      'foam-rolling': { categoryId: 'flexibility', subcategoryId: 'self-myofascial-release' },
      
      // 🎯 TYPE FIX (Nov 2025): Added missing recovery types
      'active-recovery': { 
        categoryId: 'recovery',
        fallback: { label: 'Active Recovery', color: 'bg-green-100 text-green-800', intensity: 'light' }
      },
      'massage-therapy': { 
        categoryId: 'recovery',
        fallback: { label: 'Massage Therapy', color: 'bg-green-100 text-green-800', intensity: 'light' }
      },
      'sauna-cold': { 
        categoryId: 'recovery',
        fallback: { label: 'Temperature Therapy', color: 'bg-green-100 text-green-800', intensity: 'light' }
      },
      'meditation': { 
        categoryId: 'recovery',
        fallback: { label: 'Meditation', color: 'bg-green-100 text-green-800', intensity: 'light' }
      },
      
      // 🎯 TYPE FIX (Nov 2025): Added missing functional training types
      'movement-prep': { 
        categoryId: 'functional',
        fallback: { label: 'Movement Prep', color: 'bg-indigo-100 text-indigo-800', intensity: 'light' }
      },
      'corrective-exercise': { 
        categoryId: 'functional',
        fallback: { label: 'Corrective Exercise', color: 'bg-indigo-100 text-indigo-800', intensity: 'moderate' }
      },
      'balance-stability': { 
        categoryId: 'functional',
        fallback: { label: 'Balance & Stability', color: 'bg-indigo-100 text-indigo-800', intensity: 'moderate' }
      },
      'agility-training': { 
        categoryId: 'functional',
        fallback: { label: 'Agility Training', color: 'bg-indigo-100 text-indigo-800', intensity: 'moderate' }
      },
      
      // Legacy types with fallbacks
      'cardio': { 
        categoryId: 'cardio', 
        fallback: { label: 'Cardio', color: 'bg-red-100 text-red-800', intensity: 'moderate' }
      },
      'strength': { 
        categoryId: 'strength', 
        fallback: { label: 'Strength', color: 'bg-purple-100 text-purple-800', intensity: 'moderate' }
      },
      'flexibility': {
        categoryId: 'flexibility',
        fallback: { label: 'Flexibility', color: 'bg-pink-100 text-pink-800', intensity: 'light' }
      },
      'recovery': {
        categoryId: 'recovery',
        fallback: { label: 'Recovery', color: 'bg-green-100 text-green-800', intensity: 'light' }
      },
      'hiit': {
        categoryId: 'cardio',
        fallback: { label: 'HIIT', color: 'bg-orange-100 text-orange-800', intensity: 'high' }
      },
      'sports': {
        categoryId: 'sports',
        fallback: { label: 'Sports', color: 'bg-blue-100 text-blue-800', intensity: 'moderate' }
      }
    };

    const mapping = typeMapping[workoutType];
    if (!mapping) {
      // Return a default configuration for unknown types
      return {
        label: workoutType.charAt(0).toUpperCase() + workoutType.slice(1),
        color: 'bg-gray-100 text-gray-800',
        intensity: 'moderate'
      };
    }

    if (mapping.fallback) {
      return mapping.fallback;
    }

    if (mapping.subcategoryId) {
      const subcategory = this.getSubcategoryById(mapping.categoryId, mapping.subcategoryId);
      const category = this.getCategoryById(mapping.categoryId);
      
      if (subcategory && category) {
        return {
          label: subcategory.name,
          color: category.color,
          description: subcategory.description,
          tags: subcategory.tags,
          intensity: subcategory.intensity,
          duration: subcategory.duration
        };
      }
    }

    // Fallback to category level
    const category = this.getCategoryById(mapping.categoryId);
    if (category) {
      return {
        label: category.name,
        color: category.color,
        description: category.description
      };
    }

    // Final fallback
    return {
      label: workoutType.charAt(0).toUpperCase() + workoutType.slice(1),
      color: 'bg-gray-100 text-gray-800',
      intensity: 'moderate'
    };
  }

  /**
   * Get workout type configuration for UI
   */
  static getWorkoutTypeConfig(categoryId: string, subcategoryId?: string) {
    const category = this.getCategoryById(categoryId);
    if (!category) return null;
    
    if (subcategoryId) {
      const subcategory = this.getSubcategoryById(categoryId, subcategoryId);
      return {
        label: subcategory?.name || category.name,
        color: category.color,
        description: subcategory?.description || category.description,
        tags: subcategory?.tags || [],
        intensity: subcategory?.intensity,
        duration: subcategory?.duration
      };
    }
    
    return {
      label: category.name,
      color: category.color,
      description: category.description
    };
  }
}